// SignupPage.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';

export default function SignupPage() {
  const [step, setStep] = useState(1); // 1: Account Info, 2: Select Plan
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const [selectedTier, setSelectedTier] = useState('pro'); // 'starter', 'pro', 'super_pro'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Pricing data matching backend
  const pricingTiers = {
    starter: {
      name: 'Starter',
      monthly: { price: 18.95, priceId: 'price_1SR09vKrDWVDJRBkzpYB4MAh' },
      annual: { price: 215.10, priceId: 'price_1SR0BsKrDWVDJRBkbM8UMg9Q', savings: '5%' },
      credits: 160,
      features: [
        '160 credits/month (1,920/year)',
        'All AI models',
        'Image & video generation',
        'Document export',
        'Email support',
      ]
    },
    pro: {
      name: 'Pro',
      monthly: { price: 24.95, priceId: 'price_1SR0CgKrDWVDJRBkCP1p2JZ7' },
      annual: { price: 284.10, priceId: 'price_1SR0DqKrDWVDJRBkfVvQBPeM', savings: '5%' },
      credits: 320,
      popular: true,
      features: [
        '320 credits/month (3,840/year)',
        'All AI models',
        'Priority processing',
        'Advanced features',
        'Priority support',
        'No rate limits',
      ]
    },
    super_pro: {
      name: 'Super Pro',
      monthly: { price: 49.95, priceId: 'price_1SR0EsKrDWVDJRBkRpGJv94z' },
      annual: { price: 569.43, priceId: 'price_1SR0FkKrDWVDJRBkLggs2Sut', savings: '5%' },
      credits: 400,
      features: [
        '400 credits/month (4,800/year)',
        'All models + beta access',
        'Fastest processing',
        'Custom workflows',
        'Dedicated support',
        'API access',
        'Team features',
      ]
    }
  };

  const handleAccountCreation = async (e) => {
    e.preventDefault();
    
    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setError('');
    // Move to plan selection
    setStep(2);
  };

  const hashEmail = async (email) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');
    
    try {
      // 1. Generate hash
      const userHash = await hashEmail(email);
      
      // 2. Check if already subscribed to QXAI
      const statusResponse = await fetch(
        'https://vtaas-backend-220419304601.us-central1.run.app/api/stripe/subscription-status',
        { headers: { 'X-User-Hash': userHash }}
      );
      const status = await statusResponse.json();
      
      if (status.status === 'active') {
        setError('You already have an active QXAI subscription! Redirecting to login...');
        setTimeout(() => window.location.href = '/signin', 2000);
        return;
      }
      
      // 3. Check what auth methods this email has
      const methods = await fetchSignInMethodsForEmail(auth, email);
      
      if (methods.length === 0) {
        // New user - create account
        await createUserWithEmailAndPassword(auth, email, password);
      } else if (methods.includes('password')) {
        // Has password - sign in
        await signInWithEmailAndPassword(auth, email, password);
      } else if (methods.includes('google.com')) {
        // GemIQ user with Google only
        setError(
          "QXAI creates a unique privacy hash from your email - no Google accounts, no tracking, no surveillance.\n\n" +
          "This email is already registered with GemIQ. To maintain QXAI's privacy architecture, please use a different email for your secure QXAI hash.\n\n" +
          "Privacy isn't a feature. It's our foundation."
        );
        setLoading(false);
        return;
      }
      
      localStorage.setItem('qxai_user_hash', userHash);
      
      // 4. Redirect to Stripe
      const tier = pricingTiers[selectedTier];
      const priceId = billingCycle === 'monthly' ? tier.monthly.priceId : tier.annual.priceId;
      
      const checkoutResponse = await fetch(
        'https://vtaas-backend-220419304601.us-central1.run.app/api/stripe/create-checkout',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Hash': userHash,
            'X-Platform': 'qxai'
          },
          body: JSON.stringify({
            priceId,
            type: 'subscription',
            successUrl: `${window.location.origin}/success`,
            cancelUrl: `${window.location.origin}/signup`
          })
        }
      );
      
      if (!checkoutResponse.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      const { sessionUrl } = await checkoutResponse.json();
      window.location.href = sessionUrl;
      
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message || 'Signup failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Header */}
      <header className="flex justify-between items-center p-6 border-b border-gray-800">
        <div 
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate('/')}
        >
          <img src="/qxai-logo.png" alt="QuantumXAI" className="h-10" />
        </div>
        <button
          onClick={() => navigate('/signin')}
          className="text-gray-400 hover:text-white transition"
        >
          Already have an account? <span className="text-blue-500 font-semibold">Sign in</span>
        </button>
      </header>

      {/* Progress Indicator */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-500' : 'text-gray-600'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
              step >= 1 ? 'border-blue-500 bg-blue-500' : 'border-gray-600'
            }`}>
              {step > 1 ? '✓' : '1'}
            </div>
            <span className="font-medium">Account Info</span>
          </div>
          <div className={`h-px w-16 ${step >= 2 ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-500' : 'text-gray-600'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
              step >= 2 ? 'border-blue-500 bg-blue-500' : 'border-gray-600'
            }`}>
              2
            </div>
            <span className="font-medium">Select Plan</span>
          </div>
        </div>

        {/* Step 1: Account Creation - EMAIL/PASSWORD ONLY */}
        {step === 1 && (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-3">Create Your Account</h1>
              <p className="text-gray-400">Join the privacy-first AI revolution</p>
            </div>

            <form onSubmit={handleAccountCreation} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition"
                />
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <span>🔒</span> Your email is hashed locally and never stored on our servers
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition"
                />
                <p className="text-xs text-gray-500 mt-2">Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 transition"
                />
              </div>

              {/* Terms and Privacy Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  required
                  className="mt-1 h-4 w-4 rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="terms-checkbox" className="text-sm text-gray-400">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
              >
                Continue to Plan Selection →
              </button>
            </form>

            {/* Privacy Features */}
            <div className="mt-8 grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl mb-1">🔒</div>
                <p className="text-xs text-gray-400">Hash-based auth</p>
              </div>
              <div>
                <div className="text-2xl mb-1">🚫</div>
                <p className="text-xs text-gray-400">No tracking</p>
              </div>
              <div>
                <div className="text-2xl mb-1">💾</div>
                <p className="text-xs text-gray-400">Local storage</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Plan Selection */}
        {step === 2 && (
          <div>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-3">Choose Your Plan</h1>
              <p className="text-gray-400">All plans include 5% savings with annual billing</p>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-md transition ${
                    billingCycle === 'monthly'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-md transition relative ${
                    billingCycle === 'annual'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Annual
                  <span className="absolute -top-2 -right-2 bg-green-500 text-xs px-2 py-0.5 rounded-full text-white">
                    Save 5%
                  </span>
                </button>
              </div>
            </div>

            {error && (
              <div className="max-w-4xl mx-auto mb-6 bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-8">
              {Object.entries(pricingTiers).map(([key, tier]) => {
                const price = billingCycle === 'monthly' ? tier.monthly : tier.annual;
                const monthlyPrice = billingCycle === 'annual' ? price.price / 12 : price.price;
                
                return (
                  <div
                    key={key}
                    onClick={() => setSelectedTier(key)}
                    className={`relative bg-gray-800 rounded-xl p-6 cursor-pointer transition border-2 ${
                      selectedTier === key
                        ? 'border-blue-500 shadow-lg shadow-blue-500/20'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                        MOST POPULAR
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-bold">${monthlyPrice.toFixed(2)}</span>
                        <span className="text-gray-400">/mo</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-xs text-green-500 mt-1">
                          ${price.price.toFixed(2)} billed annually
                        </p>
                      )}
                      {billingCycle === 'monthly' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Billed monthly
                        </p>
                      )}
                    </div>

                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <span className="text-green-500 mt-0.5">✓</span>
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      className={`w-full py-3 rounded-lg font-semibold transition ${
                        selectedTier === key
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      }`}
                    >
                      {selectedTier === key ? 'Selected ✓' : 'Select Plan'}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setStep(1)}
                className="px-8 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition"
                disabled={loading}
              >
                ← Back
              </button>
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Continue to Payment →'}
              </button>
            </div>

            {/* Trust Signals */}
            <div className="mt-8 text-center text-sm text-gray-500">
              <p>🔒 Secure payment processing by Stripe</p>
              <p className="mt-2">Cancel anytime • No hidden fees • 100% privacy guaranteed</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
